// A streamed message is rendered as one editor per top-level markdown block. Re-importing
// markdown rebuilds every node in the editor, so finished blocks have to keep their text
// unchanged while the stream grows, and only the last block may change.

import { decodeCitedText, STREAMING_QUOTE } from "@/components/richtext/Citation/streaming";

const BLOCK_SEPARATOR = /\n(?:[ \t]*\n)+/;

// Lenient on purpose: a chunk that is only `-` or `1` is probably the first token of a list item.
const LIST_ITEM_START = /^[ \t]*(?:[-*+]|\d+[.)]?)(?:[ \t]|$)/;
const LIST_ITEM_LINE = /^[ \t]*(?:[-*+]|\d+[.)])(?:[ \t]|$)/;
const INDENTED_START = /^[ \t]+\S/;
const HEADING_LINE = /^[ \t]{0,3}#{1,6}(?:[ \t]|$)/;

// Every prefix of `${A|E|D:id[, start, end[, "cited text"]]}` that is still missing its closing brace.
// The body grammar matches CITATION_REGEX in components/richtext/Citation.
const CITATION_PREFIX =
    /^\$(?:\{(?:[AED](?::(?:\d+(?:,\s*(?:\d+(?:,\s*(?:\d+(?:,\s*(?:"(?:[^"\\}]|\\[\s\S]|"(?!\}))*\\?)?)?)?)?)?)?)?)?)?)?$/;
const QUOTE_OPENED = /^\$\{[AED]:\d+,\s*\d+,\s*\d+,\s*"/;
const UNESCAPED_TAIL = /(?:^|[^\\])(?:\\\\)*[\\"]$/;

/** Index where an unfinished citation starts at the end of `text`, or -1. */
export function partialCitationStart(text: string): number {
    for (let i = text.indexOf("$"); i !== -1; i = text.indexOf("$", i + 1)) {
        if (i + 1 < text.length && text[i + 1] !== "{") continue;
        if (CITATION_PREFIX.test(text.slice(i))) return i;
    }
    return -1;
}

/**
 * Prepare the end of streamed text for display, so raw citation syntax is never shown. A citation
 * whose quote has opened is closed around STREAMING_QUOTE and its quote so far is returned
 * separately, so the markdown stays the same while the quote grows. Any other unfinished citation
 * (still in its ids and line numbers) is dropped.
 */
export function streamingCitationTail(text: string): { text: string; quote: string | null } {
    const start = partialCitationStart(text);
    if (start === -1) return { text, quote: null };
    const open = QUOTE_OPENED.exec(text.slice(start));
    if (!open) return { text: text.slice(0, start).trimEnd(), quote: null };
    let raw = text.slice(start + open[0].length);
    // An unescaped trailing backslash starts an escape, and an unescaped trailing quote may be the closing one.
    if (UNESCAPED_TAIL.test(raw)) raw = raw.slice(0, -1);
    return { text: `${text.slice(0, start)}${open[0]}${STREAMING_QUOTE}"}`, quote: decodeCitedText(raw) };
}

function lastLine(text: string): string {
    return text.slice(text.lastIndexOf("\n") + 1);
}

function continuesPrevious(prev: string, next: string): boolean {
    if (partialCitationStart(prev) !== -1) return true;
    if (INDENTED_START.test(next)) return true;
    const prevLine = lastLine(prev);
    // A heading sits closer to what follows it than to what precedes it.
    if (HEADING_LINE.test(prevLine)) return true;
    if (LIST_ITEM_START.test(next) && (LIST_ITEM_LINE.test(prevLine) || INDENTED_START.test(prevLine))) return true;
    if (next.startsWith("|") && prevLine.startsWith("|")) return true;
    return false;
}

/**
 * Split markdown into top-level blocks at blank lines. Chunks that belong to the block before
 * them (loose list items, indented continuations, the content under a heading, a citation whose
 * quote spans a blank line) stay joined, so each block renders as it would inside the whole message.
 */
export function splitMarkdownBlocks(text: string): string[] {
    const blocks: string[] = [];
    for (const chunk of text.split(BLOCK_SEPARATOR)) {
        if (!chunk.trim()) continue;
        const prev = blocks[blocks.length - 1];
        if (prev !== undefined && continuesPrevious(prev, chunk)) {
            blocks[blocks.length - 1] = `${prev}\n\n${chunk}`;
        } else {
            blocks.push(chunk);
        }
    }
    return blocks;
}
